import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import { STUCK_SENDING_REASON, stuckSendNote } from "@/lib/stuck-send";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Workflow, Plus, RefreshCw, Trash2, ArrowLeft, Save, Play, Pause, FileText, ChevronUp, ChevronDown, ChevronRight, X, Webhook, Zap, Copy, Download, Search, Mail, Users,
} from "lucide-react";

const CATEGORIES = ["tip", "tutorial", "subscription", "other"];
const CADENCE_TYPES = ["relative", "weekly", "after_duration"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DURATION_UNITS = [
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const StatusBadge = ({ status }) => {
  const map = {
    draft: "bg-slate-100 text-slate-700",
    active: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
  };
  return <Badge className={`${map[status] || map.draft} hover:${map[status] || map.draft}`}>{status}</Badge>;
};

// Format as DD/MM/YY (Australian convention — matches the rest of the app's
// date display on Sydney-anchored cadences).
const formatAUDate = (d) => {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

// Compute the next occurrence of (weekday, send_time) at-or-after a given
// "from" time in the user's local timezone. Our weekday convention is
// 0=Mon..6=Sun; JS Date.getDay() uses 0=Sun..6=Sat, hence the conversion.
const nextWeeklyOccurrenceFrom = (weekday, sendTime, fromDate) => {
  const from = fromDate ? new Date(fromDate) : new Date();
  const target = new Date(from);
  const jsTarget = (Number(weekday) + 1) % 7;
  const [hh, mm] = (sendTime || "09:00").split(":").map(Number);
  target.setHours(hh, mm, 0, 0);
  let daysAhead = (jsTarget - from.getDay() + 7) % 7;
  if (daysAhead === 0 && target <= from) daysAhead = 7;
  target.setDate(target.getDate() + daysAhead);
  return target;
};

// Convenience for "from now"
const nextWeeklyOccurrence = (weekday, sendTime) =>
  nextWeeklyOccurrenceFrom(weekday, sendTime, new Date());

const stepSummary = (step, idx) => {
  if (step.cadence_type === "relative") {
    return `Step ${idx + 1}: ${step.delay_days === 0 ? "immediately" : `${step.delay_days} day${step.delay_days === 1 ? "" : "s"} after previous`} at ${step.send_time}`;
  }
  if (step.cadence_type === "after_duration") {
    const value = step.delay_value ?? 0;
    const unit = step.delay_unit || "minutes";
    const unitLabel = value === 1 ? unit.replace(/s$/, "") : unit;
    return `Step ${idx + 1}: ${value} ${unitLabel} after enrolment`;
  }
  // Weekly — show the actual next-occurrence date so the user can confirm
  // when the email will fire (e.g. "Tue 05/05/26 at 14:00 (Sydney)").
  const day = WEEKDAYS[step.weekday];
  if (day == null || step.weekday == null) {
    return `Step ${idx + 1}: every ? at ${step.send_time} (Sydney)`;
  }
  const next = nextWeeklyOccurrence(step.weekday, step.send_time);
  return `Step ${idx + 1}: ${day} ${formatAUDate(next)} at ${step.send_time} (Sydney)`;
};

// ---- Webhook setup card (Phase 3c) ----
const WebhookSetupCard = ({ sequence, authHeaders, onSyncDone }) => {
  const [syncing, setSyncing] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const webhookUrl = `${window.location.origin.replace(/\/$/, "")}/api/hubspot/webhook`;

  const copyUrl = () => {
    navigator.clipboard?.writeText(webhookUrl);
    toast.success("Webhook URL copied");
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sequences/${sequence.id}/sync-now`, {}, { withCredentials: true, headers: authHeaders });
      const r = res.data;
      const skipped = (r.skipped_opt_out || 0) + (r.skipped_non_marketing || 0) + (r.skipped_already_in || 0) + (r.skipped_other || 0);
      toast.success(`Synced ${r.list_size} contacts: enrolled ${r.enrolled}, skipped ${skipped}, cancelled ${r.cancelled_orphans}`, { duration: 7000 });
      onSyncDone && onSyncDone();
    } catch (err) {
      toast.error(formatErrorDetail(err, "Sync failed"));
    } finally { setSyncing(false); }
  };

  if (!sequence?.hubspot_list_id) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-indigo-600" /> HubSpot list sync</span>
          <Button variant="secondary" size="sm" onClick={handleSyncNow} disabled={syncing}>
            {syncing ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Syncing…</> : <><RefreshCw className="w-4 h-4 mr-1" /> Sync now</>}
          </Button>
        </CardTitle>
        <CardDescription>
          Pulls current list members + cancels enrolments for contacts no longer in the list. Runs automatically when you first activate a sequence.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="ghost" size="sm" onClick={() => setShowSetup(!showSetup)}>
          <Webhook className="w-4 h-4 mr-1" /> {showSetup ? "Hide" : "Show"} real-time webhook setup
        </Button>
        {showSetup && (
          <div className="mt-3 space-y-2 text-sm bg-slate-50 border border-slate-200 rounded p-4">
            <p className="text-slate-700"><strong>One-off setup in HubSpot</strong> (skip if you only want manual Sync now):</p>
            <ol className="list-decimal list-inside text-slate-600 space-y-1 text-xs">
              <li>HubSpot → Settings → Integrations → Private Apps → your app → <strong>Webhooks</strong> tab.</li>
              <li>Paste this URL into <strong>Target URL</strong>:
                <div className="flex items-center gap-2 mt-1 ml-1">
                  <code className="text-xs bg-white border border-slate-200 px-2 py-1 rounded font-mono break-all">{webhookUrl}</code>
                  <Button variant="ghost" size="sm" onClick={copyUrl}><Copy className="w-3 h-3" /></Button>
                </div>
                <p className="text-amber-600 text-xs ml-1 mt-1">
                  Note: this URL must be reachable from the public internet. For local dev, use a tunnel (cloudflared / ngrok) or just use the Sync now button.
                </p>
              </li>
              <li>Create a subscription: <strong>Contact → Property changed</strong> filtered to properties <code className="text-xs bg-white px-1 rounded">hs_email_optout</code> and <code className="text-xs bg-white px-1 rounded">hs_marketable_status</code>.</li>
              <li>Create another subscription: <strong>Contact → Association changed</strong>.</li>
              <li>Auth tab → copy <strong>Client secret</strong> → paste into <code className="text-xs bg-white px-1 rounded">backend/.env</code> as <code className="text-xs bg-white px-1 rounded">HUBSPOT_WEBHOOK_SECRET</code>, restart backend.</li>
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Editor ----
// ---- Recipients tab (per-sequence roster) ----

const SKIP_REASON_LABEL = {
  opted_out: "Opted out",
  not_marketable: "Non-marketing",
  suppressed: "Suppressed",
  missing_email: "No email",
};

const CANCEL_REASON_LABEL = {
  opted_out: "Opted out",
  not_marketable: "Non-marketing",
  suppressed: "Suppressed",
  removed_from_list: "Removed from list",
  template_deleted: "Template deleted",
  manual: "Cancelled manually",
  bounced: "Hard bounced",
};

const GROUP_META = [
  { key: "active", label: "Active — queued", color: "emerald", icon: Mail },
  { key: "paused", label: "Paused", color: "amber", icon: Pause },
  { key: "completed", label: "Completed", color: "sky", icon: Workflow },
  { key: "cancelled", label: "Cancelled at send time", color: "red", icon: X },
  { key: "skipped", label: "Skipped at enrolment", color: "slate", icon: Users },
];

const formatRecipientDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const RecipientDrillInModal = ({ open, onOpenChange, recipient, sequenceStepCount, authHeaders }) => {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !recipient || recipient.kind !== "enrollment") {
      setSends([]); return;
    }
    setLoading(true);
    axios.get(`${API}/email-sends`, {
      params: { enrollment_id: recipient.id, limit: 50, sort_by: "sent_at", sort_order: "asc" },
      withCredentials: true, headers: authHeaders,
    })
      .then((res) => setSends(res.data.sends || []))
      .catch((err) => toast.error(formatErrorDetail(err, "Failed to load history")))
      .finally(() => setLoading(false));
  }, [open, recipient, authHeaders]);

  if (!recipient) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{recipient.recipient_name || recipient.recipient_email}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {recipient.recipient_email}
            {recipient.hs_contact_id && <> · HubSpot ID {recipient.hs_contact_id}</>}
          </DialogDescription>
        </DialogHeader>

        {recipient.kind === "skip" ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
            <div className="font-medium text-amber-900">Skipped at enrolment</div>
            <div className="text-xs text-amber-800 mt-1">
              Reason: <strong>{SKIP_REASON_LABEL[recipient.reason] || recipient.reason}</strong>
            </div>
            <div className="text-xs text-amber-700 mt-1">
              Last evaluated: {formatRecipientDate(recipient.last_skipped_at)}
            </div>
            <div className="text-xs text-amber-700 mt-2">
              This contact will be re-evaluated automatically on the next sync. If they re-subscribe in HubSpot or come off the suppression list, they'll be enrolled then.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
              <div><span className="text-slate-500 inline-block w-24">Status</span> <strong>{recipient.status}</strong></div>
              <div><span className="text-slate-500 inline-block w-24">Enrolled</span> {formatRecipientDate(recipient.enrolled_at)}</div>
              {recipient.next_send_at && <div><span className="text-slate-500 inline-block w-24">Next send</span> {formatRecipientDate(recipient.next_send_at)}</div>}
              {recipient.current_step_index != null && (
                <div>
                  <span className="text-slate-500 inline-block w-24">Step</span>
                  {recipient.current_step_index + 1}{sequenceStepCount ? ` of ${sequenceStepCount}` : ""}
                </div>
              )}
              {recipient.completed_at && <div><span className="text-slate-500 inline-block w-24">Completed</span> {formatRecipientDate(recipient.completed_at)}</div>}
              {recipient.cancelled_at && <div><span className="text-slate-500 inline-block w-24">Cancelled</span> {formatRecipientDate(recipient.cancelled_at)}</div>}
              {recipient.cancelled_reason && (
                <div><span className="text-slate-500 inline-block w-24">Reason</span> {CANCEL_REASON_LABEL[recipient.cancelled_reason] || recipient.cancelled_reason}</div>
              )}
            </div>

            {recipient.paused_reason === STUCK_SENDING_REASON && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="stuck-send-note">
                <div className="font-medium">Stuck mid-send, paused for review</div>
                <div className="mt-1">
                  A crash or redeploy interrupted a send on {formatRecipientDate(recipient.stuck_send?.sending_started_at)}.{" "}
                  {stuckSendNote(recipient.stuck_send)} Resume or cancel it from Email Tools → Enrolments.
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-medium text-slate-700 mb-2">Email history</div>
              {loading ? (
                <p className="text-slate-500 text-xs">Loading…</p>
              ) : sends.length === 0 ? (
                <p className="text-slate-500 text-xs">No emails sent yet.</p>
              ) : (
                <ol className="space-y-2">
                  {sends.map((s) => (
                    <li key={s.id} className="border border-slate-200 rounded p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">
                          Step {(s.step_index ?? 0) + 1}
                          {s.template_name && <span className="text-slate-500 font-normal"> — {s.template_name}</span>}
                        </span>
                        <Badge variant="outline" className="text-[10px]">{s.status || "pending"}</Badge>
                      </div>
                      <div className="text-slate-500 mt-1">{formatRecipientDate(s.sent_at)}</div>
                      {s.error_message && <div className="text-red-600 mt-1 break-words">{s.error_message}</div>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RecipientsTab = ({ sequenceId, sequenceName, stepCount, authHeaders }) => {
  const [group, setGroup] = useState("active");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [counts, setCounts] = useState({ active: 0, paused: 0, completed: 0, cancelled: 0, skipped: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [openCard, setOpenCard] = useState({ active: true, paused: false, completed: false, cancelled: false, skipped: false });
  const [drillIn, setDrillIn] = useState(null);

  // Debounce search box
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(h);
  }, [q]);

  // Reset page when group/search changes
  useEffect(() => { setPage(1); }, [group, debouncedQ]);

  const fetchRows = useMemo(() => async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sequences/${sequenceId}/recipients`, {
        params: { group, q: debouncedQ, limit: pageSize, offset: (page - 1) * pageSize },
        withCredentials: true, headers: authHeaders,
      });
      setRows(res.data.rows || []);
      setCounts(res.data.counts || {});
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load recipients"));
    } finally {
      setLoading(false);
    }
  }, [sequenceId, group, debouncedQ, page, pageSize, authHeaders]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleExport = async (g) => {
    try {
      const res = await axios.get(`${API}/sequences/${sequenceId}/recipients/export`, {
        params: { group: g, q: debouncedQ },
        withCredentials: true, headers: authHeaders, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const safeName = (sequenceName || "sequence").replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `${safeName}_${g}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Export failed"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={fetchRows} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="space-y-2">
        {GROUP_META.map((g) => {
          const isOpen = openCard[g.key];
          const count = counts[g.key] || 0;
          const isActive = group === g.key;
          const Icon = g.icon;
          return (
            <Card key={g.key} className={isActive ? "border-indigo-300" : ""}>
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                onClick={() => {
                  const willOpen = !isOpen;
                  setOpenCard((p) => ({ ...p, [g.key]: willOpen }));
                  if (willOpen) setGroup(g.key);
                }}
              >
                <div className="flex items-center gap-3">
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  <Icon className={`w-4 h-4 text-${g.color}-600`} />
                  <span className="text-sm font-medium text-slate-900">{g.label}</span>
                  <Badge variant="secondary" className="text-xs">{count}</Badge>
                </div>
                {count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleExport(g.key); }}
                    title="Export this group as CSV"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                )}
              </button>

              {isOpen && (
                <CardContent className="pt-0">
                  {isActive && loading ? (
                    <p className="text-slate-500 text-sm py-4">Loading…</p>
                  ) : isActive && rows.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4">No recipients{debouncedQ ? " match this search" : ""}.</p>
                  ) : !isActive ? (
                    <p className="text-slate-400 text-xs py-2">Click to view rows for this group.</p>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Recipient</TableHead>
                            {g.key === "active" && <TableHead>Step</TableHead>}
                            {g.key === "active" && <TableHead>Next send</TableHead>}
                            {g.key === "paused" && <TableHead>Paused</TableHead>}
                            {g.key === "completed" && <TableHead>Completed</TableHead>}
                            {g.key === "cancelled" && <TableHead>Reason</TableHead>}
                            {g.key === "cancelled" && <TableHead>Cancelled</TableHead>}
                            {g.key === "skipped" && <TableHead>Reason</TableHead>}
                            {g.key === "skipped" && <TableHead>Last evaluated</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((r) => (
                            <TableRow
                              key={r.id}
                              className="cursor-pointer hover:bg-slate-50"
                              onClick={() => setDrillIn(r)}
                            >
                              <TableCell>
                                <div className="text-sm font-medium text-slate-900">{r.recipient_name}</div>
                                <div className="text-xs text-slate-500">{r.recipient_email}</div>
                              </TableCell>
                              {g.key === "active" && (
                                <TableCell className="text-xs">
                                  {r.current_step_index != null ? `${r.current_step_index + 1}${stepCount ? ` of ${stepCount}` : ""}` : "—"}
                                </TableCell>
                              )}
                              {g.key === "active" && (
                                <TableCell className="text-xs text-slate-600">{formatRecipientDate(r.next_send_at)}</TableCell>
                              )}
                              {g.key === "paused" && (
                                <TableCell className="text-xs">
                                  {formatRecipientDate(r.paused_at)}
                                  {r.paused_reason === STUCK_SENDING_REASON && (
                                    <Badge variant="outline" className="ml-2 text-xs text-amber-700 border-amber-300">Stuck mid-send</Badge>
                                  )}
                                </TableCell>
                              )}
                              {g.key === "completed" && <TableCell className="text-xs">{formatRecipientDate(r.completed_at)}</TableCell>}
                              {g.key === "cancelled" && (
                                <TableCell><Badge variant="outline" className="text-xs">{CANCEL_REASON_LABEL[r.cancelled_reason] || r.cancelled_reason || "—"}</Badge></TableCell>
                              )}
                              {g.key === "cancelled" && <TableCell className="text-xs">{formatRecipientDate(r.cancelled_at)}</TableCell>}
                              {g.key === "skipped" && (
                                <TableCell><Badge variant="outline" className="text-xs">{SKIP_REASON_LABEL[r.reason] || r.reason}</Badge></TableCell>
                              )}
                              {g.key === "skipped" && <TableCell className="text-xs">{formatRecipientDate(r.last_skipped_at)}</TableCell>}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>Rows per page</span>
                            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v, 10)); setPage(1); }}>
                              <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-slate-500">Page {page} of {totalPages}</span>
                            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                              <ChevronUp className="w-4 h-4 -rotate-90" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
                              <ChevronDown className="w-4 h-4 -rotate-90" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <RecipientDrillInModal
        open={!!drillIn}
        onOpenChange={(o) => !o && setDrillIn(null)}
        recipient={drillIn}
        sequenceStepCount={stepCount}
        authHeaders={authHeaders}
      />
    </div>
  );
};


// Roll consecutive steps with the same multi_group_id back into a single
// editor row so the multi-template grouping survives save → reload. Steps
// without a multi_group_id flow through unchanged.
const groupMultiTemplateSteps = (rawSteps) => {
  const out = [];
  let current = null;
  for (const s of rawSteps || []) {
    const gid = s.multi_group_id;
    if (gid && current && current.multi_group_id === gid) {
      current.multi_template_ids.push(s.template_id);
    } else {
      if (current) out.push(current);
      if (gid) {
        current = {
          multi: true,
          multi_group_id: gid,
          multi_template_ids: [s.template_id],
          template_id: s.template_id,
          cadence_type: "weekly",
          weekday: s.weekday,
          send_time: s.send_time || "09:00",
          delay_days: null,
          delay_value: null,
          delay_unit: null,
        };
      } else {
        current = null;
        out.push(s);
      }
    }
  }
  if (current) out.push(current);
  return out;
};

const SequenceEditor = ({ sequence, templates, hubspotLists, onCancel, onSaved, authHeaders, defaultAutoSyncMinutes }) => {
  const isNew = !sequence?.id;
  const [name, setName] = useState(sequence?.name || "");
  const [description, setDescription] = useState(sequence?.description || "");
  const [category, setCategory] = useState(sequence?.category || "tip");
  const [hubspotListId, setHubspotListId] = useState(sequence?.hubspot_list_id || "");
  const [steps, setSteps] = useState(() => groupMultiTemplateSteps(sequence?.steps));
  const [saving, setSaving] = useState(false);
  // -1 in the UI / on the wire = "inherit from settings default". null in
  // the DB. The dropdown stays in -1 until the user picks something explicit.
  const [autoSyncMinutes, setAutoSyncMinutes] = useState(
    sequence?.auto_sync_minutes == null ? -1 : sequence.auto_sync_minutes
  );
  const [reEnrollOnReEntry, setReEnrollOnReEntry] = useState(
    sequence?.re_enroll_on_re_entry == null ? true : !!sequence.re_enroll_on_re_entry
  );
  // When run_parallel is on, every top-level step (a multi-template group
  // counts as one) runs simultaneously from enrolment time instead of
  // waiting for the prior step to finish.
  const [runParallel, setRunParallel] = useState(!!sequence?.run_parallel);

  // Pre-compute fire dates for every template across every step so the UI
  // can show realistic dates that thread through prior steps' completion
  // (sequential mode) or anchor each step at "now" independently (parallel
  // mode). Recomputed when steps / cadence / parallel toggle change.
  const schedulePreview = useMemo(() => {
    const out = [];
    let cursor = new Date();
    for (const s of steps) {
      const startFrom = runParallel ? new Date() : cursor;
      const fires = [];
      if (s.multi) {
        const ids = (s.multi_template_ids || []).filter(Boolean);
        if (s.weekday != null) {
          let from = startFrom;
          for (let t = 0; t < ids.length; t++) {
            const occ = nextWeeklyOccurrenceFrom(s.weekday, s.send_time, from);
            fires.push({ template_id: ids[t], at: occ });
            from = new Date(occ.getTime() + 60_000);
          }
        }
      } else if (s.cadence_type === "weekly" && s.weekday != null) {
        fires.push({ template_id: s.template_id, at: nextWeeklyOccurrenceFrom(s.weekday, s.send_time, startFrom) });
      } else if (s.cadence_type === "relative") {
        const occ = new Date(startFrom);
        occ.setDate(occ.getDate() + (s.delay_days ?? 0));
        const [hh, mm] = (s.send_time || "09:00").split(":").map(Number);
        occ.setHours(hh, mm, 0, 0);
        fires.push({ template_id: s.template_id, at: occ });
      } else if (s.cadence_type === "after_duration") {
        const value = Number(s.delay_value || 0);
        const unit = s.delay_unit || "minutes";
        const ms = value * (unit === "minutes" ? 60_000 : unit === "hours" ? 3_600_000 : 86_400_000);
        fires.push({ template_id: s.template_id, at: new Date(startFrom.getTime() + ms) });
      }
      out.push(fires);
      if (fires.length > 0) {
        cursor = new Date(fires[fires.length - 1].at.getTime() + 60_000);
      }
    }
    return out;
  }, [steps, runParallel]);

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        template_id: templates[0]?.id || "",
        cadence_type: "relative",
        delay_days: prev.length === 0 ? 0 : 3,
        weekday: 1,
        send_time: "09:00",
        delay_value: 30,
        delay_unit: "minutes",
        // Multi-template mode: when multi=true, multi_template_ids holds an
        // ordered list of templates that all fire on the same weekly cadence,
        // one per occurrence of the chosen weekday. On save the list is
        // expanded into N normal sequence steps.
        multi: false,
        multi_template_ids: [],
      },
    ]);
  };
  const updateStep = (idx, patch) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const removeStep = (idx) => {
    setSteps((prev) => prev.filter((_, i) => i !== idx));
  };
  const moveStep = (idx, dir) => {
    setSteps((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (steps.length === 0) { toast.error("Add at least one step"); return; }
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      if (s.multi) {
        const ids = (s.multi_template_ids || []).filter(Boolean);
        if (ids.length === 0) { toast.error(`Step ${i + 1}: pick at least one template`); return; }
        if (s.weekday == null || s.weekday < 0 || s.weekday > 6) { toast.error(`Step ${i + 1}: pick a weekday`); return; }
      } else {
        if (!s.template_id) { toast.error(`Step ${i + 1}: pick a template`); return; }
        if (s.cadence_type === "relative" && (s.delay_days == null || s.delay_days < 0)) { toast.error(`Step ${i + 1}: delay days must be 0 or more`); return; }
        if (s.cadence_type === "weekly" && (s.weekday == null || s.weekday < 0 || s.weekday > 6)) { toast.error(`Step ${i + 1}: pick a weekday`); return; }
        if (s.cadence_type === "after_duration" && (s.delay_value == null || s.delay_value <= 0)) { toast.error(`Step ${i + 1}: duration must be greater than 0`); return; }
      }
    }
    setSaving(true);
    try {
      // Expand multi-template steps into N normal weekly steps in order.
      // Each emitted step shares the same weekday + send_time; the existing
      // weekly cadence logic pushes each one to the next occurrence after
      // the previous step fired, so the chain walks through the templates
      // one-per-week (or whatever weekday is set).
      const expandedSteps = [];
      for (const s of steps) {
        if (s.multi) {
          const ids = (s.multi_template_ids || []).filter(Boolean);
          // Reuse the existing group_id when editing so the grouping is
          // stable across edits; otherwise mint a fresh one.
          const groupId = s.multi_group_id || `mg_${Math.random().toString(36).slice(2, 12)}`;
          for (const tid of ids) {
            expandedSteps.push({
              template_id: tid,
              cadence_type: "weekly",
              delay_days: null,
              weekday: Number(s.weekday),
              send_time: s.send_time || "09:00",
              delay_value: null,
              delay_unit: null,
              multi_group_id: groupId,
            });
          }
        } else {
          expandedSteps.push({
            template_id: s.template_id,
            cadence_type: s.cadence_type,
            delay_days: s.cadence_type === "relative" ? Number(s.delay_days) : null,
            weekday: s.cadence_type === "weekly" ? Number(s.weekday) : null,
            send_time: s.send_time || "09:00",
            delay_value: s.cadence_type === "after_duration" ? Number(s.delay_value) : null,
            delay_unit: s.cadence_type === "after_duration" ? s.delay_unit : null,
            multi_group_id: null,
          });
        }
      }
      const body = {
        name: name.trim(),
        description: description.trim(),
        category,
        hubspot_list_id: hubspotListId.trim() || null,
        auto_sync_minutes: Number(autoSyncMinutes),  // -1 = use default
        re_enroll_on_re_entry: !!reEnrollOnReEntry,
        run_parallel: !!runParallel,
        steps: expandedSteps,
      };
      const res = isNew
        ? await axios.post(`${API}/sequences`, body, { withCredentials: true, headers: authHeaders })
        : await axios.put(`${API}/sequences/${sequence.id}`, body, { withCredentials: true, headers: authHeaders });
      toast.success(isNew ? "Sequence created" : "Sequence updated");
      onSaved(res.data);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  // Setup vs Recipients tabs only show when editing an existing sequence —
  // new sequences only have the Setup form (nothing to roster yet).
  const setupContent = (
    <>
      <Card>
        <CardHeader><CardTitle className="text-base">Basics</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="seq-name">Name</Label>
              <Input id="seq-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Builder welcome series" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seq-cat">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="seq-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="seq-desc">Description (internal)</Label>
              <Textarea id="seq-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this series is for" className="min-h-[60px]" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="seq-list">HubSpot list (optional — used by Phase 3c auto-enrol)</Label>
              <Select value={hubspotListId || "__none__"} onValueChange={(v) => setHubspotListId(v === "__none__" ? "" : v)}>
                <SelectTrigger id="seq-list"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {(hubspotLists || []).map((l) => (
                    <SelectItem key={l.list_id} value={l.list_id}>
                      {l.name} <span className="text-slate-400 text-xs ml-1">· {l.list_id}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auto-sync card — only meaningful when a HubSpot list is set */}
      {hubspotListId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">List sync</CardTitle>
            <CardDescription>
              How often Storm Pad re-checks the HubSpot list for this sequence. New contacts get enrolled, contacts no longer in the list get cancelled.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Auto-sync interval</Label>
                <Select value={String(autoSyncMinutes)} onValueChange={(v) => setAutoSyncMinutes(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="-1">
                      Use settings default{defaultAutoSyncMinutes != null ? ` (${defaultAutoSyncMinutes ? `every ${defaultAutoSyncMinutes} min` : "Off"})` : ""}
                    </SelectItem>
                    <SelectItem value="0">Off</SelectItem>
                    <SelectItem value="1">Every 1 minute</SelectItem>
                    <SelectItem value="5">Every 5 minutes</SelectItem>
                    <SelectItem value="10">Every 10 minutes</SelectItem>
                    <SelectItem value="15">Every 15 minutes</SelectItem>
                    <SelectItem value="30">Every 30 minutes</SelectItem>
                    <SelectItem value="60">Every 60 minutes</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  1-minute is best for testing only. Use 10–15 min for normal operation.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Re-enrol on re-entry</Label>
                <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reEnrollOnReEntry}
                    onChange={(e) => setReEnrollOnReEntry(e.target.checked)}
                    className="mt-1 w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-slate-900">Re-enrol contacts who left and re-entered the list</div>
                    <div className="text-xs text-slate-500 mt-1">
                      When on, a contact whose enrolment was cancelled because they were removed from the list gets a fresh enrolment if they re-enter the list later. Enrolments cancelled for any other reason (by hand, unsubscribed, bounced, failed sends, deleted template) stay cancelled. Off = once cancelled, never auto-re-enrolled.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {!isNew && sequence?.last_synced_at && (
              <div className="text-xs text-slate-500 border-t pt-3">
                <strong>Last synced:</strong> {formatDate(sequence.last_synced_at)}
                {sequence.last_sync_error && (
                  <div className="mt-1 text-red-600">
                    <strong>Last error:</strong> {sequence.last_sync_error}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Steps</span>
            <div className="flex items-center gap-2">
              <Button
                variant={runParallel ? "default" : "outline"}
                size="sm"
                onClick={() => setRunParallel((p) => !p)}
                className={runParallel ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}
                title="When ON, every top-level step runs simultaneously from enrolment time instead of waiting for the previous step to finish."
              >
                <Workflow className="w-4 h-4 mr-1" /> {runParallel ? "Parallel ON" : "Run in parallel"}
              </Button>
              <Button onClick={addStep} variant="secondary" size="sm" disabled={!templates.length}>
                <Plus className="w-4 h-4 mr-1" /> Add step
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            {templates.length === 0 ? (
              <span className="text-amber-600">You need to create at least one Email Template before building a sequence.</span>
            ) : runParallel ? (
              "Parallel mode: every top-level step runs simultaneously from enrolment time. Cadence still applies within each step (e.g. multi-template steps still walk one-per-week)."
            ) : (
              "Cadence applies to when each email fires. All times are Sydney. Steps run sequentially — each one waits for the previous to complete."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {steps.length === 0 && <p className="text-slate-500 text-sm">No steps yet — click "Add step".</p>}
          {steps.map((s, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-4 space-y-3 bg-white">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Step {idx + 1}</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant={s.multi ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      const enteringMulti = !s.multi;
                      updateStep(idx, {
                        multi: enteringMulti,
                        // entering multi mode: seed with the current single template
                        multi_template_ids: enteringMulti
                          ? (s.multi_template_ids?.length ? s.multi_template_ids : (s.template_id ? [s.template_id] : []))
                          : s.multi_template_ids,
                        // multi mode is always weekly
                        cadence_type: enteringMulti ? "weekly" : s.cadence_type,
                        // Initialize weekday + send_time if they're null on the
                        // step (common when the step was originally created as
                        // 'relative' cadence — the Select shows 'Tue' as a
                        // fallback but the underlying state is null until the
                        // user actually picks something, which fails validation).
                        weekday: enteringMulti ? (s.weekday == null ? 1 : s.weekday) : s.weekday,
                        send_time: enteringMulti ? (s.send_time || "09:00") : s.send_time,
                      });
                    }}
                    className={s.multi ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}
                    title="Toggle multi-template mode (one weekly cadence, many templates in order)"
                  >
                    <FileText className="w-4 h-4 mr-1" /> {s.multi ? "Multi-template ON" : "Multi-template"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => moveStep(idx, -1)} disabled={idx === 0}><ChevronUp className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1}><ChevronDown className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => removeStep(idx)} className="text-red-600"><X className="w-4 h-4" /></Button>
                </div>
              </div>

              {/* Multi-template mode: single weekly cadence, ordered list of
                  templates. Each template emits one normal weekly step on save. */}
              {s.multi ? (
                <div className="space-y-3">
                  <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                    Multi-template mode: each template fires on the next occurrence of the chosen weekday, in order. 5 templates on Tuesday at 2 PM = first email next Tuesday, second the following Tuesday, and so on.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-3 space-y-1">
                      <Label className="text-xs">When</Label>
                      <div className="h-10 px-3 flex items-center text-sm text-slate-600 border border-slate-200 rounded-md bg-slate-50">Weekly on…</div>
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Weekday</Label>
                      <Select value={String(s.weekday ?? 1)} onValueChange={(v) => updateStep(idx, { weekday: parseInt(v, 10) })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Send time</Label>
                      <Input type="time" value={s.send_time || "09:00"} onChange={(e) => updateStep(idx, { send_time: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Templates (in order)</Label>
                    {(s.multi_template_ids || []).map((tid, tIdx) => {
                      // Look up the precomputed date — accounts for prior
                      // steps' completion in sequential mode, or anchors at
                      // "now" in parallel mode.
                      const day = WEEKDAYS[s.weekday];
                      let dateLabel = "";
                      const fire = schedulePreview[idx]?.[tIdx]?.at;
                      if (fire && day != null) {
                        dateLabel = `${day} ${formatAUDate(fire)}`;
                      }
                      return (
                        <div key={tIdx} className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 w-6 text-right">{tIdx + 1}.</span>
                          <Select
                            value={tid}
                            onValueChange={(v) => {
                              const next = [...(s.multi_template_ids || [])];
                              next[tIdx] = v;
                              updateStep(idx, { multi_template_ids: next });
                            }}
                          >
                            <SelectTrigger className="w-1/2"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                            <SelectContent>
                              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-slate-400 text-xs">· {t.category}</span></SelectItem>)}
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-slate-500 flex-1">
                            {dateLabel ? `${dateLabel} at ${s.send_time}` : ""}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const next = (s.multi_template_ids || []).filter((_, i) => i !== tIdx);
                              updateStep(idx, { multi_template_ids: next });
                            }}
                            disabled={(s.multi_template_ids || []).length <= 1}
                            className="text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateStep(idx, {
                        multi_template_ids: [...(s.multi_template_ids || []), templates[0]?.id || ""],
                      })}
                      disabled={!templates.length}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add template
                    </Button>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-5 space-y-1">
                  <Label className="text-xs">Template</Label>
                  <Select value={s.template_id} onValueChange={(v) => updateStep(idx, { template_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-slate-400 text-xs">· {t.category}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3 space-y-1">
                  <Label className="text-xs">When</Label>
                  <Select value={s.cadence_type} onValueChange={(v) => updateStep(idx, { cadence_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relative">After N days</SelectItem>
                      <SelectItem value="weekly">Weekly on…</SelectItem>
                      <SelectItem value="after_duration">After duration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {s.cadence_type === "relative" && (
                  <>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Days {idx === 0 ? "after enrolment" : "after previous"}</Label>
                      <Input type="number" min="0" value={s.delay_days ?? 0} onChange={(e) => updateStep(idx, { delay_days: parseInt(e.target.value, 10) || 0 })} />
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Send time</Label>
                      <Input type="time" value={s.send_time || "09:00"} onChange={(e) => updateStep(idx, { send_time: e.target.value })} />
                    </div>
                  </>
                )}
                {s.cadence_type === "weekly" && (
                  <>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Weekday</Label>
                      <Select value={String(s.weekday ?? 1)} onValueChange={(v) => updateStep(idx, { weekday: parseInt(v, 10) })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Send time</Label>
                      <Input type="time" value={s.send_time || "09:00"} onChange={(e) => updateStep(idx, { send_time: e.target.value })} />
                    </div>
                  </>
                )}
                {s.cadence_type === "after_duration" && (
                  <>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">After enrolment</Label>
                      <Input
                        type="number"
                        min="1"
                        value={s.delay_value ?? 30}
                        onChange={(e) => updateStep(idx, { delay_value: parseInt(e.target.value, 10) || 0 })}
                      />
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Unit</Label>
                      <Select value={s.delay_unit || "minutes"} onValueChange={(v) => updateStep(idx, { delay_unit: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DURATION_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
              )}
              <p className="text-xs text-slate-500">
                {s.multi ? (() => {
                  const count = (s.multi_template_ids || []).filter(Boolean).length;
                  const day = WEEKDAYS[s.weekday];
                  if (count === 0 || s.weekday == null || day == null) {
                    return `Step ${idx + 1}: ${count} template${count === 1 ? "" : "s"} — set a weekday + at least one template to save`;
                  }
                  const firstFire = schedulePreview[idx]?.[0]?.at;
                  const dateStr = firstFire ? `${day} ${formatAUDate(firstFire)}` : `${day}`;
                  return `Step ${idx + 1}: ${count} template${count === 1 ? "" : "s"} starting ${dateStr} at ${s.send_time} (Sydney), then weekly — saves as ${count} step${count === 1 ? "" : "s"}`;
                })() : (() => {
                  // Non-multi: prefer the precomputed date so weekly steps
                  // beyond step 0 show the correct chained-schedule date in
                  // sequential mode.
                  const fire = schedulePreview[idx]?.[0]?.at;
                  if (s.cadence_type === "weekly" && fire) {
                    const day = WEEKDAYS[s.weekday];
                    if (day) return `Step ${idx + 1}: ${day} ${formatAUDate(fire)} at ${s.send_time} (Sydney)`;
                  }
                  return stepSummary(s, idx);
                })()}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* HubSpot sync card — only when editing an existing sequence with a list set */}
      {!isNew && sequence?.id && (
        <WebhookSetupCard sequence={sequence} authHeaders={authHeaders} />
      )}

      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onCancel}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h2 className="text-xl font-semibold text-slate-900">{isNew ? "New sequence" : `Edit: ${sequence?.name}`}</h2>
      </div>

      {isNew ? setupContent : (
        <Tabs defaultValue="setup">
          <TabsList>
            <TabsTrigger value="setup">Setup</TabsTrigger>
            <TabsTrigger value="recipients">Recipients</TabsTrigger>
          </TabsList>
          <TabsContent value="setup" className="space-y-6 pt-4">{setupContent}</TabsContent>
          <TabsContent value="recipients" className="pt-4">
            <RecipientsTab
              sequenceId={sequence.id}
              sequenceName={sequence.name}
              stepCount={(sequence.steps || []).length}
              authHeaders={authHeaders}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

// ---- List view ----
const Sequences = ({ user, setUser }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [hubspotLists, setHubspotLists] = useState([]);
  const [defaultAutoSyncMinutes, setDefaultAutoSyncMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, t, l, settings] = await Promise.all([
        axios.get(`${API}/sequences`, { withCredentials: true, headers: authHeaders }),
        axios.get(`${API}/email-templates`, { withCredentials: true, headers: authHeaders }),
        axios.get(`${API}/hubspot/lists`, { params: { allowed_only: true }, withCredentials: true, headers: authHeaders }).catch(() => ({ data: { lists: [] } })),
        axios.get(`${API}/settings`, { withCredentials: true, headers: authHeaders }).catch(() => ({ data: {} })),
      ]);
      setSequences(s.data.sequences || []);
      setTemplates(t.data.templates || []);
      setHubspotLists(l.data.lists || []);
      setDefaultAutoSyncMinutes(settings.data?.default_auto_sync_minutes ?? 0);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load"));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    localStorage.removeItem("token"); navigate("/");
  };

  const handleDelete = async (seq) => {
    try {
      await axios.delete(`${API}/sequences/${seq.id}`, { withCredentials: true, headers: authHeaders });
      toast.success("Sequence deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) { toast.error(formatErrorDetail(err, "Delete failed")); }
  };

  const handleStatusChange = async (seq, newStatus) => {
    try {
      const res = await axios.put(`${API}/sequences/${seq.id}`, { status: newStatus }, { withCredentials: true, headers: authHeaders });
      const backfill = res.data?.backfill_summary;
      if (backfill && backfill.error) {
        toast.warning(`Sequence ${newStatus}, but auto-sync failed: ${backfill.error}`);
      } else if (backfill) {
        toast.success(`Sequence ${newStatus}. Auto-synced list: enrolled ${backfill.enrolled}, skipped ${(backfill.skipped_opt_out||0)+(backfill.skipped_non_marketing||0)+(backfill.skipped_already_in||0)+(backfill.skipped_other||0)}, cancelled ${backfill.cancelled_orphans}`, { duration: 7000 });
      } else {
        toast.success(`Sequence ${newStatus}`);
      }
      fetchAll();
    } catch (err) { toast.error(formatErrorDetail(err, "Status change failed")); }
  };

  const handleSyncNow = async (seq, e) => {
    e?.stopPropagation();
    try {
      const res = await axios.post(`${API}/sequences/${seq.id}/sync-now`, {}, { withCredentials: true, headers: authHeaders });
      const r = res.data;
      const skipped = (r.skipped_opt_out||0)+(r.skipped_non_marketing||0)+(r.skipped_already_in||0)+(r.skipped_other||0);
      toast.success(`Synced ${r.list_size} contacts: enrolled ${r.enrolled}, skipped ${skipped}, cancelled ${r.cancelled_orphans}`, { duration: 7000 });
      fetchAll();
    } catch (err) { toast.error(formatErrorDetail(err, "Sync failed")); }
  };

  // Open the editor pre-filled with the source sequence's settings as a NEW
  // (unsaved) sequence — id stripped, status reset to draft (the create
  // endpoint forces this anyway), runtime fields cleared, and the name
  // prefixed with "Copy of …" so the user can rename before saving.
  const handleDuplicate = (seq) => {
    const dup = {
      // intentionally omit id, last_synced_at, last_sync_error,
      // created_at, updated_at, created_by_user_id, enrollment_counts
      name: `Copy of ${seq.name || "sequence"}`,
      description: seq.description || "",
      category: seq.category || "tip",
      hubspot_list_id: seq.hubspot_list_id || null,
      auto_sync_minutes: seq.auto_sync_minutes ?? null,
      re_enroll_on_re_entry: seq.re_enroll_on_re_entry ?? true,
      steps: (seq.steps || []).map((s) => ({
        template_id: s.template_id,
        cadence_type: s.cadence_type,
        delay_days: s.delay_days ?? null,
        weekday: s.weekday ?? null,
        send_time: s.send_time || "09:00",
        delay_value: s.delay_value ?? null,
        delay_unit: s.delay_unit ?? null,
      })),
    };
    setEditing(dup);
    toast.info(`Editing a copy. Rename and save to create a new sequence.`);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sequences-page">
      <Sidebar user={user} onLogout={handleLogout} />
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Sequences
            </h1>
            <p className="text-slate-500 mt-1">
              Chain templates into automated email programs. Pause stops the whole sequence; resume picks up where each contact left off.
            </p>
          </div>
          {!editing && (
            <Button onClick={() => setEditing({})} className="bg-indigo-600 hover:bg-indigo-700" disabled={!templates.length}>
              <Plus className="w-4 h-4 mr-2" /> New Sequence
            </Button>
          )}
        </div>

        {editing ? (
          <SequenceEditor
            sequence={editing}
            templates={templates}
            hubspotLists={hubspotLists}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); fetchAll(); }}
            authHeaders={authHeaders}
            defaultAutoSyncMinutes={defaultAutoSyncMinutes}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><Workflow className="w-5 h-5 text-indigo-600" /> All sequences</span>
                <Button variant="ghost" size="sm" onClick={fetchAll} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
              </CardTitle>
              <CardDescription>{sequences.length} sequence{sequences.length === 1 ? "" : "s"}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-slate-500 text-sm">Loading…</p>
              ) : !templates.length ? (
                <p className="text-slate-500 text-sm">
                  Create at least one Email Template first — sequences chain templates together.
                </p>
              ) : sequences.length === 0 ? (
                <p className="text-slate-500 text-sm">No sequences yet — click "New Sequence" to start.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Steps</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sequences.map((seq) => (
                      <TableRow key={seq.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium cursor-pointer" onClick={() => setEditing(seq)}>{seq.name}</TableCell>
                        <TableCell><Badge variant="outline">{seq.category}</Badge></TableCell>
                        <TableCell><StatusBadge status={seq.status} /></TableCell>
                        <TableCell className="text-sm">{seq.steps?.length || 0}</TableCell>
                        <TableCell className="text-sm">{seq.enrollment_counts?.active || 0}</TableCell>
                        <TableCell className="text-sm">{seq.enrollment_counts?.completed || 0}</TableCell>
                        <TableCell className="text-slate-500 text-xs">{formatDate(seq.updated_at)}</TableCell>
                        <TableCell className="text-right space-x-1">
                          {seq.status === "active" && seq.hubspot_list_id && (
                            <Button variant="ghost" size="sm" onClick={(e) => handleSyncNow(seq, e)} title="Sync now from HubSpot list">
                              <RefreshCw className="w-4 h-4 text-indigo-600" />
                            </Button>
                          )}
                          {seq.status === "draft" && (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(seq, "active")}>
                              <Play className="w-4 h-4 text-emerald-600" />
                            </Button>
                          )}
                          {seq.status === "active" && (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(seq, "paused")}>
                              <Pause className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                          {seq.status === "paused" && (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(seq, "active")}>
                              <Play className="w-4 h-4 text-emerald-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDuplicate(seq); }}
                            title="Duplicate this sequence"
                          >
                            <Copy className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(seq)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sequence?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.name}" will be permanently deleted. Will fail if any active or paused enrolments exist — cancel them first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(confirmDelete)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sequences;
